import { Capacitor } from '@capacitor/core';
import OneSignal, { LogLevel } from '@onesignal/capacitor-plugin';
import { Purchases } from '@revenuecat/purchases-capacitor';

// OneSignal is the customer-engagement layer replacing HubSpot: email, marketing
// push and in-app messages. Reminders keep using the app's own push pipeline
// (pushRegistration.ts), so this file never asks for notification permission.
// The OneSignal user is keyed by external_id = Doqit user id, the same id we hand
// to RevenueCat. RevenueCat itself matches through the $onesignalUserId subscriber
// attribute, so after login we hand OneSignal's own id back to RevenueCat; that is
// what lets RevenueCat tag the OneSignal user with subscription state.

// The OneSignal app id is public (it ships inside every build and is visible
// to anyone who opens the bundle), so it does not need to travel through the
// build environment. 1.139 went out with the variable missing from the
// release build settings and the SDK silently never started; a fallback here
// means a missing variable can no longer switch marketing push off.
const DEFAULT_APP_ID = '1b437a80-0987-4453-9fdc-a521184a6f66';
const appId = process.env.REACT_APP_ONESIGNAL_APP_ID || DEFAULT_APP_ID;
let initialised = false;

const enabled = () => Capacitor.isNativePlatform() && !!appId;

// The tap handler lives in onesignalClicks.ts (attached from app.tsx right
// after initOneSignal): it needs eventTriggers, which imports this file.

export const initOneSignal = (): void => {
  if (!enabled() || initialised) return;
  try {
    if (process.env.NODE_ENV !== 'production') {
      OneSignal.Debug.setLogLevel(LogLevel.Warn);
    }
    OneSignal.initialize(appId as string);
    initialised = true;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('[onesignal] initialise failed', e);
  }
};

// Call whenever the app knows who the user is: sign in, session restore,
// verification, MFA, password reset. Safe to call repeatedly.
// OneSignal assigns its own id asynchronously after login; hand it to RevenueCat as
// soon as it exists (immediately when cached, otherwise on the next user change).
const linkRevenueCat = async (): Promise<void> => {
  const push = async (id: string | null) => {
    if (!id) return;
    try {
      await Purchases.setOnesignalUserID({ onesignalUserID: id });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[onesignal] RevenueCat link failed', e);
    }
  };
  const id = await OneSignal.User.getOnesignalId();
  if (id) {
    await push(id);
    return;
  }
  const once = (event: { current: { onesignalId?: string | null } }) => {
    if (event.current.onesignalId) {
      OneSignal.User.removeEventListener('change', once);
      push(event.current.onesignalId);
    }
  };
  OneSignal.User.addEventListener('change', once);
};

export const oneSignalLogin = (userId: string | number | undefined | null): void => {
  if (!enabled() || userId === undefined || userId === null || userId === '') return;
  try {
    initOneSignal();
    OneSignal.login(String(userId));
    linkRevenueCat().catch((e) => {
      // eslint-disable-next-line no-console
      console.error('[onesignal] RevenueCat link failed', e);
    });
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('[onesignal] login failed', e);
  }
};

export const oneSignalLogout = (): void => {
  if (!enabled() || !initialised) return;
  try {
    OneSignal.logout();
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('[onesignal] logout failed', e);
  }
};

// App moments worth a real-time Journey trigger in OneSignal. Everything else stays
// Mixpanel-only and reaches OneSignal as synced cohorts. Names are snake_case for
// OneSignal; the Mixpanel names are the source of truth.
const MIRRORED_EVENTS: Record<string, string> = {
  Register: 'signed_up',
  'Onboarding - Started': 'onboarding_started',
  'Onboarding - Upload Skipped': 'onboarding_upload_skipped',
  'Onboarding - Document Created': 'onboarding_document_created',
  'Onboarding - Completed': 'onboarding_completed',
  'Add Document': 'document_added',
  'Smart Scan - Completed': 'smart_scan_completed',
  'Reminders - Added': 'reminder_added',
  'Document - Shared': 'document_shared',
  'Paywall - Viewed': 'paywall_viewed',
  'Purchase - Started': 'purchase_started',
  'Purchase - Cancelled': 'purchase_cancelled',
  'Purchase - Failed': 'purchase_failed',
  'Subscription - Cancelled': 'subscription_cancelled_in_app',
  'Account - Deleted': 'account_deleted',
};

// Mirror a Mixpanel event to OneSignal as a custom event when it is on the list above.
// Only string/number/boolean props travel; fire-and-forget like everything else here.
export const oneSignalMirrorEvent = (mixpanelName: string, props: Record<string, unknown> = {}): void => {
  const name = MIRRORED_EVENTS[mixpanelName];
  if (!name || !enabled() || !initialised) return;
  try {
    const clean: Record<string, string | number | boolean> = {};
    Object.entries(props).forEach(([k, v]) => {
      if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') clean[k] = v;
    });
    OneSignal.User.trackEvent(name, clean).catch(() => undefined);
  } catch {
    // never let tracking throw into product code
  }
};
